/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import https from 'https';
import getConfig from '../../config';

declare let adobeIMS: AdobeIMS;

class AppHomeCapabilityService {
	static instance: AppHomeCapabilityService;

	/**
	 * Get supported browsers json from external web app
	 * @param {string} app sapcode of app to get browser json
	 * @returns object - json with unsupported browser
	 */
	async getSupportedBrowsers(app: string) {
		const supportedApps = ['PHSP'];

		let supportedBrowsers;
		if (supportedApps.includes(app)) {
			const response = await this._performCapabilityServiceRequest(app);
			if (response instanceof Error) {
				supportedBrowsers = response;
			}
			else {
				supportedBrowsers = response.browsers;
			}
		}
		return supportedBrowsers;
	}

	/*
	**
	**  P R I V A T E   M E T H O D S
	**
	*/
	constructor() {
		if (!AppHomeCapabilityService.instance) {
			AppHomeCapabilityService.instance = this;
		}
		return AppHomeCapabilityService.instance;
	}

	/**
	 * Perform get request to web app capability endpoints
	 * @returns {Object}
	 */
	_performCapabilityServiceRequest(app: string): Promise<any> {
		return new Promise((resolve, reject) => {
			const obj = {
				hostname: getConfig('SERVICES').apphomecapability[`${app}`],
				path: getConfig('SERVICES').apphomecapability.capabilityPath,
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
					Accept: '*/*',
					'x-api-key': getConfig('CLIENT_ID'),
					authorization: `Bearer ${adobeIMS.getAccessToken()}`,
				},
			};
			const request = https.request(
				obj,
				(res) => {
					if (res.statusCode && res.statusCode >= 300) {
						window.cchome.util.logger.error(
							'AppHome Capability Service',
							`Problem with AppHome Capability Service response: status code ${res.statusCode}`,
							res,
						);
						const CapabilityServiceError = new Error('_performCapabilityServiceRequest error');
						reject(CapabilityServiceError);
					}
					else {
						let data = '';
						res.setEncoding('utf8');
						res.on('data', (chunk) => {
							data += chunk;
						});
						res.on('end', () => {
							try {
								resolve(JSON.parse(data));
							}
							catch (e: any) {
								window.cchome.util.logger.error(
									'AppHome Capability Service',
									'Problem with parsing AppHome Capability Service response',
									e.toString(),
								);
								reject(new Error('AppHomeCapabilityService parse error'));
							}
						});
					}
				},
			);

			request.on('error', (err) => {
			// on error, display the search error message
				window.cchome.util.logger.error(
					'AppHome Capability Service',
					'Problem with AppHome Capability Service request',
					err.toString(),
				);
				const CapabilityServiceError = new Error('_performCapabilityServiceRequest error');
				reject(CapabilityServiceError);
			});

			request.end();
		});
	}
}

export default AppHomeCapabilityService;
